import {
  Breakpoints,
  Spacing,
  BorderRadius,
  ZIndex,
  ThemeMode,
  Shadows,
  Theme,
} from '../types/theme';
// 'styled-components' 대신 선언되는 타입 파일에서 타입을 가져오므로 직접적인 import는 필요 없음
// 대신 styled-components 충돌을 방지하기 위해 해당 import 제거

// 브레이크포인트 (px)
export const breakpoints: Breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// 간격
export const spacing: Spacing = {
  unit: 8,
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
};

// 모서리 둥글기
export const borderRadius: BorderRadius = {
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '12px',
  xl: '16px',
  round: '50%',
};

// Z-인덱스
export const zIndex: ZIndex = {
  mobileStepper: 1000,
  fab: 1050,
  speedDial: 1050,
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

// 그림자
export const shadows: Shadows = {
  0: 'none',
  1: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
  2: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
  3: '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)',
  4: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
  8: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
  16: '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)',
  24: '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)',
};

// 트랜지션 인터페이스 정의
export interface Transitions {
  easing: {
    easeInOut: string;
    easeOut: string;
    easeIn: string;
    sharp: string;
  };
  duration: {
    shortest: number;
    shorter: number;
    short: number;
    standard: number;
    complex: number;
    enteringScreen: number;
    leavingScreen: number;
  };
}

// 미니멀 비즈니스 스타일 팔레트
export const minimalPalette = {
  primary: {
    main: '#2563eb',
    light: '#3b82f6',
    dark: '#1e40af',
    contrastText: '#fff',
  },
  secondary: {
    main: '#64748b',
    light: '#94a3b8',
    dark: '#334155',
    contrastText: '#fff',
  },
  success: {
    main: '#22c55e',
    light: '#4ade80',
    dark: '#15803d',
    contrastText: '#fff',
  },
  error: {
    main: '#ef4444',
    light: '#f87171',
    dark: '#b91c1c',
    contrastText: '#fff',
  },
  warning: {
    main: '#f59e42',
    light: '#fbbf24',
    dark: '#b45309',
    contrastText: '#222',
  },
  info: {
    main: '#0ea5e9',
    light: '#38bdf8',
    dark: '#0369a1',
    contrastText: '#fff',
  },
  grey: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  text: {
    primary: '#222',
    secondary: '#64748b',
    disabled: '#cbd5e1',
    hint: '#94a3b8',
  },
  background: {
    default: '#fff',
    paper: '#f8fafc',
    elevated: '#fff',
  },
  divider: '#e2e8f0',
  action: {
    active: '#2563eb',
    hover: '#f1f5f9',
    selected: '#e0e7ef',
    disabled: '#cbd5e1',
    disabledBackground: '#f1f5f9',
  },
};

// 미니멀 타이포그래피
export const minimalTypography = {
  fontFamily:
    'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontWeightLight: 400,
  fontWeightRegular: 500,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' },
  h2: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.005em' },
  h3: { fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.3, letterSpacing: '0em' },
  h4: { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.35, letterSpacing: '0em' },
  h5: { fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.4, letterSpacing: '0em' },
  h6: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.4, letterSpacing: '0em' },
  subtitle1: { fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.5, letterSpacing: '0.00938em' },
  subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5, letterSpacing: '0.00714em' },
  body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0.00938em' },
  body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0.01071em' },
  button: {
    fontSize: '0.95rem',
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'none' as const,
  },
  caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.66, letterSpacing: '0.03333em' },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase' as const,
  },
};

// 라이트 테마 정의
export const lightTheme: Theme = {
  mode: 'light',
  name: 'light',
  palette: minimalPalette,
  shadows,
  typography: minimalTypography,
  spacing: (factor: number) => `${factor * 0.25}rem`,
  breakpoints,
  borderRadius,
  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
  zIndex,
};

// 다크 테마 정의
export const darkTheme: Theme = {
  mode: 'dark',
  name: 'dark',
  palette: {
    ...minimalPalette,
    grey: {
      50: '#2d3748',
      100: '#4a5568',
      200: '#718096',
      300: '#a0aec0',
      400: '#cbd5e0',
      500: '#e2e8f0',
      600: '#edf2f7',
      700: '#f7fafc',
      800: '#f9fafb',
      900: '#ffffff',
    },
    text: {
      primary: '#f7fafc',
      secondary: '#e2e8f0',
      disabled: '#a0aec0',
      hint: '#718096',
    },
    background: {
      default: '#1a202c',
      paper: '#2d3748',
      elevated: '#353f54',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    action: {
      active: 'rgba(255, 255, 255, 0.7)',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  shadows,
  typography: minimalTypography,
  spacing: (factor: number) => `${factor * 0.25}rem`,
  breakpoints,
  borderRadius,
  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
  zIndex,
};

// 시스템 테마 감지 함수
export const detectSystemTheme = (): ThemeMode => {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

// 테마 가져오기
export const getTheme = (mode: ThemeMode = 'light'): Theme => {
  return mode === 'dark' ? darkTheme : lightTheme;
};
